import React, { useEffect, useRef, useState } from "react";
import { stats, milestones } from "../data/mock";

function useCountUp(target, start, duration = 1600) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return value;
}

function StatItem({ s, start, idx }) {
  const v = useCountUp(s.value, start);
  return (
    <div className="reveal" style={{ transitionDelay: `${idx * 80}ms` }}>
      <div className="font-display text-5xl lg:text-7xl font-bold text-white tabular-nums">
        {v}
        <span className="text-[#F5D000]">{s.suffix}</span>
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-[0.25em] text-white/55">{s.label}</div>
    </div>
  );
}

export default function Stats() {
  const ref = useRef(null);
  const [start, setStart] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setStart(true)),
      { threshold: 0.3 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section id="about" ref={ref} className="relative py-24 lg:py-32 bg-[#0a0a0c] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-accent mb-4 reveal">Awesome Facts</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-6 reveal">
              A heritage of <span className="text-amber-accent">building Britain</span>, one project at a time.
            </h2>
            <p className="text-white/65 leading-relaxed reveal">
              From a small Birmingham contractor to ICE Innovation Award winners,
              R&amp;C has been quietly shaping the bridges, structures and
              steelworks that keep the Midlands moving.
            </p>

            <div className="grid grid-cols-2 gap-6 mt-10">
              {stats.map((s, i) => (
                <StatItem key={s.label} s={s} start={start} idx={i} />
              ))}
            </div>
          </div>

          {/* Right, timeline */}
          <div className="lg:col-span-7 relative">
            <div className="absolute left-3 top-2 bottom-2 w-px bg-gradient-to-b from-[#F5D000] via-white/15 to-transparent" />
            <div className="space-y-7">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className="relative pl-12 reveal"
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className="absolute left-0 top-1 w-7 h-7 rounded-full border-2 border-[#F5D000] bg-[#0a0a0c] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#F5D000]" />
                  </div>
                  <div className="flex items-baseline gap-4 flex-wrap">
                    <div className="font-display text-3xl font-bold text-[#F5D000]">{m.year}</div>
                    <div className="font-display text-xl font-semibold text-white">{m.title}</div>
                  </div>
                  <p className="text-white/65 mt-2 leading-relaxed">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
